/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/api/announcements.js
 *
 * Purpose: React Query hooks for fetching portal announcements and dismissing them per contact.
 *
 * Scope: Announcements banner/list shown to portal customers, including admin-only announcements.
 *
 * Tags: customer-portal, notifications
 *
 * Overview: `useAnnouncements` issues one query for general announcements and, when the current
 *     user is a portal admin, a second query for admin-only announcements, merging both result
 *     sets into a single flattened list along with combined loading/error state. `useAnnouncementDismiss`
 *     is a mutation that patches the contact's viewed-announcements preference and refetches the
 *     current contact ("me") query on success so the dismissed announcement disappears from view.
 *
 * Exports: useAnnouncements, useAnnouncementDismiss
 */

import { useAppStore } from 'App/store';
import { useMutation, useQueries } from 'react-query';
import { axiosinstance } from 'shared/utils/api';
import refetchMe from './contacts';

const baseUrl = '/v1/announcements';

export function useAnnouncements() {
  const { appState: { portalAdmin } } = useAppStore();
  const adminQuery = portalAdmin ? [{ admins_only: true }] : [];
  const queryFn = ({ queryKey }) =>
    axiosinstance
      .get(baseUrl, { params: queryKey.at(-1) })
      .then(({ data: res }) => res?.data);
  const generateQuery = (options) => ({
    queryKey: ['announcements', options],
    queryFn,
  });
  const announcements = useQueries(
    [{ admins_only: false }, ...adminQuery].map(generateQuery)
  );

  return {
    isError: announcements.every((a) => a?.isError),
    isLoading: announcements.every((a) => a?.isLoading),
    data: announcements.reduce((acc, { data = [] }) => [...acc, ...data], []),
  };
}

export function useAnnouncementDismiss() {
  const { mutate, ...mutationObject } = useMutation(
    (id) =>
      axiosinstance
        .patch(`/v1/contact/preferences`, { viewed_announcements: [id] })
        .then((res) => res.data),
    {
      onSuccess: refetchMe,
    }
  );

  return [mutate, mutationObject];
}
